import {
  type Dispatch,
  type FunctionComponent,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// Add to this type for future globally shared text values
export type ControlledTextID = 'risk-gap-summary-note';

export type ControlledTextContextValue<T extends ControlledTextID> = {
  /** The universal id of the interface context. Not expected to change during the lifespan of the app. */
  controlledTextId: T;
  controlledText: string | undefined | null;
  setControlledText: Dispatch<SetStateAction<string | undefined | null>>;
};

// Common value generator for specific controlled text types
const useControlledTextContextProviderValue = <T extends ControlledTextID>(
  id: T,
): ControlledTextContextValue<T> => {
  const [controlledText, setControlledText] = useState<string | undefined | null>(null);
  return {
    controlledTextId: id,
    controlledText,
    setControlledText,
  };
};

// Common type function for controlled text hooks
type UseControlledTextContextHookFn<T extends ControlledTextID> = (opts?: {
  initialValue?: string | undefined | null;
}) => ControlledTextContextValue<T>;

// <risk-gap-summary-note>

const RiskGapSummaryNoteContext = createContext<
  ControlledTextContextValue<'risk-gap-summary-note'>
>({
  controlledTextId: 'risk-gap-summary-note',
  controlledText: null,
  setControlledText: () => {
    /* noop */
  },
});

/** Use this to control the shared risk gap summary note value */
export const useRiskGapSummaryNoteContext: UseControlledTextContextHookFn<
  'risk-gap-summary-note'
> = (opts?: {
  /** Initial value is not expected to change. */
  initialValue?: string | undefined | null;
}) => {
  // acquire the context
  const { controlledTextId, controlledText, setControlledText } =
    useContext(RiskGapSummaryNoteContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We only want to trigger this useEffect on component mount
  useEffect(() => {
    // ensure the initial value is set if defined
    if (opts?.initialValue !== undefined) {
      setControlledText(opts?.initialValue);
    }
  }, []);
  const initialNoteRef = useRef(opts?.initialValue);
  useEffect(() => {
    // only update controlled note state when the defined initial note changes
    if (opts?.initialValue !== undefined && initialNoteRef.current !== opts?.initialValue) {
      initialNoteRef.current = opts?.initialValue;
      setControlledText(opts?.initialValue);
    }
  }, [opts?.initialValue, setControlledText]);

  return { controlledTextId, controlledText, setControlledText };
};

export const RiskGapSummaryNoteContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const value = useControlledTextContextProviderValue('risk-gap-summary-note');
  return <RiskGapSummaryNoteContext value={value}>{children}</RiskGapSummaryNoteContext>;
};

// </risk-gap-summary-note>
